/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, ButtonGroup, DividerPromo } from 'sarsaparilla';

export default function PassPromo({
    imageUrl,
    locationName,
    agencyCode,
    parkPassFacilityID,
    parkPassFacilityName,
}) {
    const targetRef = useRef();
    const getHeading = () => {
        if (['FS', 'USFS'].includes(agencyCode) && parkPassFacilityName) {
            return parkPassFacilityName;
        }
        if (['NPS', 'FWS', 'USFWS'].includes(agencyCode)) {
            return `${locationName} Entrance Passes`;
        }
        return `${locationName} Day Use Passes`;
    };
    const heading = getHeading();
    let copy = null;
    switch (agencyCode) {
        case 'NPS':
        case 'USFWS':
        case 'FWS':
            copy =
                'Purchase a digital site pass before your visit to cover entrance fees at this location.';
            break;
        case 'USFS':
        case 'FS':
            copy =
                'Purchase a digital site pass before your visit to cover standard amenity (day use) fees at this location.';
            break;
        case 'BLM':
            copy =
                'Purchase a digital site pass before your visit to cover day use fees at this location.';
            break;
        default:
    }

    const href = useMemo(() => {
        //For Snake River we should redirect to the corresponding TimedEntry page
        if (parkPassFacilityID === '74396') {
            return '/timed-entry/10112505';
        }
        return `/sitepass/${parkPassFacilityID}`;
    }, [parkPassFacilityID]);

    return (
        <Box marginY="xl" id="pass-promo-wrapper-id">
            <DividerPromo
                heading={heading}
                promoHeadingLevel={2}
                promoHeadingAppearance={'h3'}
                copy={copy}
                backgroundImagePath={imageUrl}
                refForIntersection={targetRef}
            >
                <ButtonGroup>
                    <Button
                        href={href}
                        aria-label={`Buy pass for ${locationName}`}
                        appearance="tertiary-white"
                        gaReportName={'Gateway Pass Promotion'}
                        gaReportCreative={agencyCode || ''}
                        gaReportPosition={`${locationName} Gateway`}
                        shouldReportToGA
                    >
                        Buy a Pass
                    </Button>
                </ButtonGroup>
            </DividerPromo>
        </Box>
    );
}

PassPromo.propTypes = {
    imageUrl: PropTypes.string,
    locationName: PropTypes.string.isRequired,
    agencyCode: PropTypes.string,
    parkPassFacilityID: PropTypes.string.isRequired,
    parkPassFacilityName: PropTypes.string,
};
